import { useMemo } from "react";

import { useMe, useUserSkills } from "./react-query";

export const PROFILE_FIELD_LABELS = {
  about: "About you",
  skills: "Skills",
  location: "Location",
  topics: "Topics you care about",
} as const;

export type ProfileMissingField = keyof typeof PROFILE_FIELD_LABELS;

export interface IProfileCompleteness {
  isAuthenticated: boolean;
  isLoading: boolean;
  isComplete: boolean;
  missingFields: ProfileMissingField[];
  missingLabels: string[];
  userId: string | undefined;
}

/**
 * Returns which of the "core" profile fields are still empty for the
 * currently logged-in user.
 *
 * Complete = About + at least one Skill + Primary Location + at least
 * one Issue Rating Category.
 */
export function useProfileCompleteness(): IProfileCompleteness {
  const { data: meResult, isLoading: meLoading } = useMe({});
  const user = meResult?.data;
  const userId = user?.id;

  const { data: skillsResult, isLoading: skillsLoading } = useUserSkills({
    id: userId,
  });
  const skills = skillsResult?.data;

  return useMemo(() => {
    const isAuthenticated = !!userId;
    const isLoading = meLoading || (!!userId && skillsLoading);

    if (!isAuthenticated || isLoading) {
      return {
        isAuthenticated,
        isLoading,
        isComplete: false,
        missingFields: [],
        missingLabels: [],
        userId,
      };
    }

    const missing: ProfileMissingField[] = [];

    if (!user?.about || !user.about.trim()) {
      missing.push("about");
    }
    if (!skills || skills.length === 0) {
      missing.push("skills");
    }
    if (!user?.placeId) {
      missing.push("location");
    }
    if (
      !user?.issueRatingCategories ||
      user.issueRatingCategories.length === 0
    ) {
      missing.push("topics");
    }

    return {
      isAuthenticated,
      isLoading,
      isComplete: missing.length === 0,
      missingFields: missing,
      missingLabels: missing.map((key) => PROFILE_FIELD_LABELS[key]),
      userId,
    };
  }, [
    userId,
    meLoading,
    skillsLoading,
    user?.about,
    user?.placeId,
    user?.issueRatingCategories,
    skills,
  ]);
}
